
/**
 * 修改输入的数据结构
 * 右侧 Triz 步骤页面点击时传递这些参数
 * 左侧聊天输入框展示并在提交时带上这些参数
 */
export interface ModifyInput {
    referenceIdeaId?: string;
    title?: string;
    description?: string;
    moduleKey?: MODULE_CONTENT_KEY;
    versionNumber?: number;
}

/**
 * 占位符输入
 * 用于预填充输入框的内容
 */
export interface PlaceholderInput {
    id: string;
    question: string;
}

export enum StepType {
    /**
     * 未开始
     */
    STEP0 = 0,
    /**
     * 问题理解
     */
    STEP1 = 1,
    /**
     * 分析问题
     */
    STEP2 = 2,
    /**
     * 生成idea
     */
    STEP3 = 3,
    /**
     * 调整solution
     */
    STEP4 = 4
}

/**
 * 模块内容key
 */
export enum MODULE_CONTENT_KEY {
     // step 1
     SYSTEM_COMPONENT_ANALYSIS = 'SYSTEM_COMPONENT_ANALYSIS',
     COMPONENT_TOUCH_ANALYSIS = 'COMPONENT_TOUCH_ANALYSIS',
     FUNCTIONAL_MODELING = 'FUNCTIONAL_MODELING',
     SYSTEM_STRUCTURE_ANALYSIS = 'SYSTEM_STRUCTURE_ANALYSIS',
     FIRST_STEP_PROBLEM_SUMMARY = 'FIRST_STEP_PROBLEM_SUMMARY',

     // step 2
     CAUSAL_CHAIN_ANALYSIS = 'CAUSAL_CHAIN_ANALYSIS',
     SECOND_STEP_PROBLEM_SUMMARY = 'SECOND_STEP_PROBLEM_SUMMARY',

     // step 3

     BATCH_PATENT_RECALL = 'BATCH_PATENT_RECALL',
     BATCH_PATENT_TRIZ_FEATURES = 'BATCH_PATENT_TRIZ_FEATURES',
     BATCH_SOLUTION_IDEAS = 'BATCH_SOLUTION_IDEAS',

     // step 4
     SOLUTION_REFINE_ANALYSIS = 'SOLUTION_REFINE_ANALYSIS',
     SOLUTION_VALIDATION_ANALYSIS = 'SOLUTION_VALIDATION_ANALYSIS',
     SOLUTION_NOVELTY_ANALYSIS = 'SOLUTION_NOVELTY_ANALYSIS',
     SOLUTION_IMAGE = 'SOLUTION_IMAGE',
     SOLUTION_VIDEO = 'SOLUTION_VIDEO',
     SOLUTION_FLOWCHART = 'SOLUTION_FLOWCHART'
}

export enum EProjectSubType {
    PROJECT_SEEK = 'PROJECT_SEEK',
    PROJECT_MIND = 'PROJECT_MIND',
    PROJECT_FMEA = 'PROJECT_FMEA',
    PROJECT_PDF = 'PROJECT_PDF',
    PROJECT_WORD = 'PROJECT_WORD',
    PROJECT_EXCEL = 'PROJECT_EXCEL'
}

export enum EContentType {
    ABST = 'ABST',
    TECHNICAL_TITLE = 'TECHNICAL_TITLE'
}

export interface AUTHOR {
    id: string;
    name: string;
}

export enum SOLUTION_TYPE {
    PATENT = 'PATENT',
    PAPER = 'PAPER',
    WEBSITE = 'WEBSITE',
    NEWS = 'NEWS',
    REPORT = 'REPORT',
    PROJECT = 'PROJECT'
}

export interface IOrgInfo {
    display_name?: string;
    logo?: string;
    id?: string;
    normalized_display_name?: string;
    normalized_id?: string;
    normalized_logo?: string
}

/**
 * 关键词类型
 */
export type KeywordType =
    | 'core_term' // 核心术语
    | 'synonym_term' // 同义术语
    | 'broader_term' // 上位术语
    | 'tech_tradeoff' // 技术权衡
    | 'expected_effect' // 期望效果
    | 'cross_domain_1' // 跨领域1
    | 'cross_domain_2' // 跨领域2
    | 'tech_solution_1' // 技术方案1
    | 'tech_solution_2' // 技术方案2
    | 'tech_solution_3'; // 技术方案3

/**
 * 关键词组
 */
export interface KeywordGroup {
    /**
     * 关键词类型
     */
    type: KeywordType;
    /**
     * 查询语句
     */
    query: string;
}

/**
 * 关键词生成消息
 */
export interface KeywordGenerationMessage {
    /**
     * 轮次
     */
    round: number;
    /**
     * 关键词组列表
     */
    keyword_group: KeywordGroup[];
}

/**
 * 专利评估结果
 */

export interface ISolution extends Record<string, any> {
    SOLUTION_ID: string;
    PATENT_ID: string;
    CONTENT: string;
    CONTENT_TYPE?: EContentType;
    TITLE?: string;
    ORG_INFO?: IOrgInfo[];
    APD?: string;
    SIMPLE_LEGAL_STATUS?: string | number;
    PDF_IMAGE_COUNT?: number;
    SOLUTION_TYPE: SOLUTION_TYPE;
    PATENT_UPDATE_TS?: number | string | Date;
    PN?: string;
    DOI?: string;
    AUTHORS?: AUTHOR[]
    WEBSITE?: {
        name: string
        url: string
    }[]
    PUBLISH_TIME?: string
    JOURNAL?: string,
    PBD?: string
    ISD?: string
    EXPD?: string
    PRIORITY_DATE?: string
    LINK?: string
    // 市场报告
    PUBLISHER?: string;
    PUBLISHED_TIME?: number;
    CATEGORY?: {
        name: string;
        id: string;
    }[];
    MARKET_INFOS?: Array<{
        normalized_market_size: number
        year: number
    }>;
    AVG_CAGR?: number;
    SOLUTION_SUB_TYPE?: EProjectSubType;
    PROJECT_ID?: string;
    PROJECT_NAME?: string;
}

export interface KeyProblem {
    select: boolean;
    problem_id: string;
    problem_description: string;
    user_friendly_description: string;
}

/**
 * 专利评估消息
 */
export interface PatentEvaluationMessage {
    /**
     * 轮次
     */
    round?: number;
    /**
     * 批次索引
     */
    batch_idx?: number;
    /**
     * 关键问题
     */
    key_problem?: KeyProblem;
    /**
     * 专利评估列表
     */
    relevant_patents: ISolution[];
}

/**
 * 功能指纹
 */
export interface FunctionFingerprint {
    /**
     * 功能载体
     */
    function_carrier: string;
    /**
     * 动作动词
     */
    action_verb: string;
    /**
     * 功能对象
     */
    function_object: string;
    /**
     * 性能水平
     */
    performance_level: string;
    /**
     * 抽象层级
     */
    abstraction_level: string;
}

/**
 * Su-Field 特征
 */
export interface SufieldFeatures {
    /**
     * 物质1
     */
    substance_1: string;
    /**
     * 物质2
     */
    substance_2: string;
    /**
     * 场类型
     */
    field_type: string;
    /**
     * 场进化水平
     */
    field_evolution_level: string;
    /**
     * 作用机制
     */
    interaction_mechanism: string;
}

/**
 * 技术矛盾
 */
export interface TechnicalContradiction {
    /**
     * 改善参数
     */
    improved_parameter: string;
    /**
     * 恶化参数
     */
    worsened_parameter: string;
    /**
     * 工程参数列表
     */
    engineering_parameters?: string[];
    /**
     * 解决原理列表
     */
    solving_principle: string[];
    /**
     * 矛盾描述
     */
    contradiction_description: string;
}

/**
 * 物理矛盾
 */
export interface PhysicalContradiction {
    /**
     * 参数名称
     */
    parameter_name: string;
    /**
     * 相反需求1
     */
    opposite_requirement_1: string;
    /**
     * 相反需求2
     */
    opposite_requirement_2: string;
    /**
     * 分离维度
     */
    separation_dimension: string;
    /**
     * 矛盾描述
     */
    contradiction_description: string;
}

/**
 * 专利 TRIZ 特征
 */
export interface PatentTrizFeature {
    /**
     * 专利号
     */
    pn: string;
    /**
     * 专利标题
     */
    title: string;
    /**
     * 专利ID
     */
    patent_id: string;
    /**
     * 功能指纹
     */
    function_fingerprint: FunctionFingerprint;
    /**
     * Su-Field 特征
     */
    sufield_features: SufieldFeatures;
    /**
     * 技术矛盾
     */
    technical_contradiction: TechnicalContradiction | null;
    /**
     * 物理矛盾
     */
    physical_contradiction: PhysicalContradiction | null;
}

/**
 * TRIZ 特征提取批次消息
 */
export interface PatentTrizFeaturesMessage {
    /**
     * 批次索引
     */
    batch_idx?: number;
    /**
     * 专利 TRIZ 特征列表
     */
    patent_triz_features: PatentTrizFeature[];
}
